import { FC } from 'react';
import { round } from 'lodash';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Column, Row } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import { CalendarDayRange } from 'further-types/utils';
import { IncomeStatementDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import PageFilters, { Filter } from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import getColumnHeaders from './helpers/getColumnHeaders';
import getColumns from './helpers/getColumns';
import getRows from './helpers/getRows';
import getCustomRow from './helpers/getCustomRow';

const pageId = 'cash-flow-statement';

const incomeStatementDateLabels: Record<IncomeStatementDateOption, string> = {
  [IncomeStatementDateOption.CurrentQuarter]: 'Current quarter',
  [IncomeStatementDateOption.LastQuarter]: 'Last quarter',
  [IncomeStatementDateOption.FinancialYearToDate]: 'Financial year-to-date',
  [IncomeStatementDateOption.LastFinancialYear]: 'Last financial year',
  [IncomeStatementDateOption.Custom]: 'Custom',
};

const incomeStatementDateOptions = Object.values(IncomeStatementDateOption).map(
  (option) => ({
    label: incomeStatementDateLabels[option],
    value: option,
  }),
);

const NetIncomeAccountCategory = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.lg),
  fontSize: '12px !important',
  color: theme.palette.grey[600],
}));

const CashFlowStatement: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { vehicle, selectedVehicleId } = useLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: IncomeStatementDateOption;
    customDateRange?: CalendarDayRange;
  }>(pageId, {
    defaultFilters: {
      dateOption: IncomeStatementDateOption.LastQuarter,
      customDateRange: undefined,
    },
  });
  const { dateOption, customDateRange } = filters;
  const isCustomDateRange = dateOption === IncomeStatementDateOption.Custom;
  const isCustomDateRangeUnset =
    isCustomDateRange &&
    (!customDateRange?.startDate || !customDateRange?.endDate);

  const customRangeComplete =
    !isCustomDateRange ||
    (!!customDateRange?.startDate && !!customDateRange?.endDate);

  const { data, isLoading, isFetching } =
    api.generalLedger.cashFlowStatement.useQuery(
      {
        vehicleId: selectedVehicleId ?? '',
        dateOption,
        customDateRange:
          isCustomDateRange && customDateRange
            ? {
                startDate: customDateRange.startDate,
                endDate: customDateRange.endDate,
              }
            : undefined,
      },
      {
        enabled:
          showOtherAccounting && !!selectedVehicleId && customRangeComplete,
      },
    );

  const exportMutation = api.generalLedger.exportCashFlowStatement.useMutation(
    useHandleTrpcMutation<{ file: string; fileName: string }>()({
      onSuccess: ({ file, fileName }) => {
        downloadXlxs(file, fileName);
      },
      invalidations: [],
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (!customRangeComplete) return;
    exportMutation.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDateRange:
        isCustomDateRange && customDateRange
          ? {
              startDate: customDateRange.startDate,
              endDate: customDateRange.endDate,
            }
          : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const today = new CalendarDay(new Date());
  const periodEndDate = data?.periodEndDate?.isAfter(today)
    ? today
    : data?.periodEndDate;
  const [header1, header2, header3] = getColumnHeaders(
    dateOption,
    data?.periodStartDate,
    data?.fyStartDate,
    periodEndDate,
    data?.inceptionDate,
  );
  const { baseCurrency } = vehicle;

  const formatValue = (value: number) => {
    const rounded = round(value, 2);
    const forDisplay = rounded === 0 ? 0 : rounded;
    return numberToCurrencyString(forDisplay, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });
  };
  const columns = getColumns({
    baseCurrency,
    header1,
    header2,
    header3,
  });
  const {
    netIncomeRows,
    netIncomeSubrows,
    netCashFromOperations,
    financingActivityRows,
    netCashFromFinancing,
    showFinancingSection,
    summaryRows,
    cashAtEnd,
  } = getRows(data);

  return (
    <Column spacing="lg" fullHeight>
      <Row centered spacing="sm" justify="space-between" breakpoint="md">
        <Column spacing="xs">
          <Typography variant="h5" color="grey.900">
            Cash flow statement
          </Typography>
          <Typography variant="body2" noWrap={!isMobile}>
            {vehicle.vehicleName} for the periods ending{' '}
            {isCustomDateRange
              ? isCustomDateRangeUnset
                ? '...'
                : customDateRange?.endDate.toReadable()
              : incomeStatementDateLabels[dateOption].toLowerCase()}
            {isCustomDateRange ? (isCustomDateRangeUnset ? '' : '.') : '.'}
          </Typography>
        </Column>
        <PageFilters
          justify="flex-end"
          align="center"
          actions={[
            {
              label: 'Export',
              variant: 'contained',
              onClick: handleExport,
              disabled: isCustomDateRangeUnset,
            },
          ]}
        >
          <Filter size="sm">
            <BasicSelect
              fullWidth
              options={incomeStatementDateOptions}
              value={dateOption}
              onChange={(newDateOption) =>
                handleChangeFilters({
                  dateOption: newDateOption,
                  customDateRange:
                    newDateOption !== IncomeStatementDateOption.Custom
                      ? undefined
                      : customDateRange,
                })
              }
              placeholder="Select date"
            />
          </Filter>
          {isCustomDateRange ? (
            <Filter size="sm">
              <DateRangePicker
                dateRange={customDateRange}
                onDateRangeChange={(dateRange) => {
                  handleChangeFilters({ customDateRange: dateRange });
                }}
                placeholder="Select custom date range"
              />
            </Filter>
          ) : null}
        </PageFilters>
      </Row>
      <SyncScrollProvider>
        <Column fullHeight>
          <AccountingTable
            columns={columns}
            rows={[]}
            isLoading={isFetching}
            headerRowLineHeight={16}
            headerRowPaddingY={8}
            bodyRowPaddingY={!data ? 30 : undefined}
            emptyMessage={!data ? 'No data.' : undefined}
          />
          {isFetching ? null : data ? (
            <>
              <AccountingSection
                key="cash-flow-from-operating-activities"
                title="Cash flow from operating activities"
                titleVariant="h6"
                expandable={false}
                noTopBorder
              >
                <AccountingTable
                  columns={columns}
                  rows={netIncomeRows}
                  bottomBorderOnly
                  hideHeader
                  customFooterRow={[
                    {
                      key: 'adjustments-to-reconcile-net-incomes-to-net-cash-provided-by-used-in-operating-activities',
                      content: (
                        <NetIncomeAccountCategory>
                          Adjustments to reconcile net income to net cash
                          provided by/(used in) operating activities.
                        </NetIncomeAccountCategory>
                      ),
                      colspan: 4,
                    },
                  ]}
                />
                <AccountingTable
                  columns={columns}
                  rows={netIncomeSubrows}
                  indentation={40}
                  borderless
                  hideHeader
                  displayFooterIfEmptyTable
                  customFooterRow={getCustomRow({
                    key: 'net-cash-provided-by-used-in-operating-activities',
                    label:
                      'Net cash provided by/(used in) operating activities',
                    period1: formatValue(netCashFromOperations.period1),
                    period2: formatValue(netCashFromOperations.period2),
                    period3: formatValue(netCashFromOperations.period3),
                  })}
                />
              </AccountingSection>
              {showFinancingSection ? (
                <AccountingSection
                  key="cash-flow-from-financing-activities"
                  title="Cash flows from financing activities"
                  titleVariant="h6"
                  expandable={false}
                  noTopBorder
                >
                  <AccountingTable
                    columns={columns}
                    rows={financingActivityRows}
                    bottomBorderOnly
                    hideHeader
                    displayFooterIfEmptyTable
                    customFooterRow={getCustomRow({
                      key: 'net-cash-used-in-financing-activities',
                      label: 'Net cash used in financing activities',
                      period1: formatValue(netCashFromFinancing.period1),
                      period2: formatValue(netCashFromFinancing.period2),
                      period3: formatValue(netCashFromFinancing.period3),
                    })}
                  />
                </AccountingSection>
              ) : null}
              <AccountingSection
                key="cash-position"
                title="Cash position"
                titleVariant="h6"
                expandable={false}
                noTopBorder
              >
                <AccountingTable
                  columns={columns}
                  rows={summaryRows}
                  borderless
                  hideHeader
                  displayFooterIfEmptyTable
                  customFooterRow={getCustomRow({
                    key: 'cash-and-cash-equivalents-at-end-of-period',
                    label: 'Cash and cash equivalents at end of period',
                    period1: formatValue(cashAtEnd.period1),
                    period2: formatValue(cashAtEnd.period2),
                    period3: formatValue(cashAtEnd.period3),
                  })}
                />
              </AccountingSection>
            </>
          ) : null}
        </Column>
      </SyncScrollProvider>
    </Column>
  );
};

export default CashFlowStatement;
